import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Info, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import KeywordMatchingSetting from './KeywordMatchingSetting';
import SectionCard from './SectionCard';
import type { TenantSettingsResponse } from '@services/settings.service';

interface SearchSettingsCardProps {
  settings: TenantSettingsResponse | null;
  loading: boolean;
  error: string | null;
  canManage: boolean;
  onToggleKeywordMatching: (next: boolean) => void;
}

const SearchSettingsCard: React.FC<SearchSettingsCardProps> = ({
  settings,
  loading,
  error,
  canManage,
  onToggleKeywordMatching,
}) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={Search} title={t('settings.searchSettings')}>
      {loading && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <CircleAlert className="h-4 w-4" />
          <AlertDescription className="revy-cjk leading-[1.6]">{error}</AlertDescription>
        </Alert>
      )}

      {settings && !loading && (
        <KeywordMatchingSetting
          enabled={settings.settings.search.keyword_matching_enabled}
          disabled={!canManage || loading}
          canManage={canManage}
          onToggle={onToggleKeywordMatching}
        />
      )}

      <Alert variant="info" className="mt-4">
        <Info className="h-4 w-4" />
        <AlertDescription className="revy-cjk text-sm leading-[1.7]">
          <strong>{t('settings.searchModesTitle')}</strong>
          <br />
          <strong>{t('settings.keywordMatchOnLabel')}:</strong>{' '}
          {t('settings.keywordMatchOnDescription')}
          <br />
          <strong>{t('settings.keywordMatchOffLabel')}:</strong>{' '}
          {t('settings.keywordMatchOffDescription')}
        </AlertDescription>
      </Alert>
    </SectionCard>
  );
};

export default SearchSettingsCard;
